import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tosShareImage } from '../utils/tosImage';
import {
  getAssetSharePreview,
  getAssetShareComments,
  createAssetShareComment,
  AssetSharePreviewData,
  AssetShareComment,
  AssetShareCharacter,
} from '../api/assetShare';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface LightboxImage {
  id: string;
  imageUrl: string;
  imageHash: string;
  role?: string;
}

interface LightboxState {
  characterName: string;
  section: string; // "casting" or state name
  images: LightboxImage[];
  index: number;
}

/* ------------------------------------------------------------------ */
/*  Responsive style tag (injected once)                               */
/* ------------------------------------------------------------------ */

const STYLE_ID = 'asset-share-preview-responsive';

function ensureResponsiveStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes spin { to { transform: rotate(360deg); } }

    .asp-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
    .asp-thumb-strip { display: flex; gap: 6px; overflow-x: auto; padding: 8px 16px; }
    .asp-thumb-strip::-webkit-scrollbar { height: 4px; }
    .asp-thumb-strip::-webkit-scrollbar-thumb { background: #444; border-radius: 2px; }

    @media (min-width: 768px) {
      .asp-page { max-width: 800px !important; }
      .asp-grid { grid-template-columns: repeat(3, 1fr); }
    }
    @media (min-width: 1024px) {
      .asp-grid { grid-template-columns: repeat(4, 1fr); }
    }
  `;
  document.head.appendChild(style);
}

/* ------------------------------------------------------------------ */
/*  Sequential image loader                                            */
/* ------------------------------------------------------------------ */

interface ImageQueueItem {
  url: string;
  resolve: () => void;
}

class SequentialImageLoader {
  private queue: ImageQueueItem[] = [];
  private loading = false;

  enqueue(url: string): Promise<void> {
    return new Promise(resolve => {
      this.queue.push({ url, resolve });
      this.flush();
    });
  }

  private flush() {
    if (this.loading || this.queue.length === 0) return;
    this.loading = true;
    const item = this.queue.shift()!;
    const img = new Image();
    img.onload = img.onerror = () => {
      item.resolve();
      this.loading = false;
      this.flush();
    };
    img.src = item.url;
  }
}

const globalImageQueue = new SequentialImageLoader();

/* ------------------------------------------------------------------ */
/*  Helper: count comments for a given image id                        */
/* ------------------------------------------------------------------ */

function countCommentsForImage(comments: AssetShareComment[], imageId: string): number {
  return comments.filter(c => c.assetImageId === imageId).length;
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

function ImageCard({
  src,
  alt,
  roleBadge,
  commentCount,
  onClick,
}: {
  src: string;
  alt: string;
  roleBadge?: string;
  commentCount: number;
  onClick: () => void;
}) {
  const [loaded, setLoaded] = useState(false);
  const [queuedSrc, setQueuedSrc] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const fullSrc = tosShareImage(src);

  useEffect(() => {
    mountedRef.current = true;
    setLoaded(false);
    setQueuedSrc(null);
    globalImageQueue.enqueue(fullSrc).then(() => {
      if (mountedRef.current) {
        setQueuedSrc(fullSrc);
        setLoaded(true);
      }
    });
    return () => { mountedRef.current = false; };
  }, [fullSrc]);

  return (
    <div style={cardStyles.wrapper} onClick={onClick}>
      {!loaded && (
        <div style={cardStyles.placeholder}>
          <div style={cardStyles.cardSpinner} />
        </div>
      )}
      {queuedSrc && (
        <img
          src={queuedSrc}
          alt={alt}
          style={{ ...cardStyles.image, opacity: loaded ? 1 : 0 }}
        />
      )}
      {roleBadge && <span style={cardStyles.roleBadge}>{roleBadge}</span>}
      {commentCount > 0 && (
        <span style={cardStyles.commentBubble}>{commentCount}</span>
      )}
    </div>
  );
}

const cardStyles: Record<string, React.CSSProperties> = {
  wrapper: {
    position: 'relative',
    borderRadius: '6px',
    overflow: 'hidden',
    cursor: 'pointer',
  },
  placeholder: {
    width: '100%',
    aspectRatio: '3/4',
    background: '#222',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardSpinner: {
    width: '20px',
    height: '20px',
    border: '2px solid rgba(255,255,255,0.15)',
    borderTopColor: '#666',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  image: {
    width: '100%',
    display: 'block',
    transition: 'opacity 0.2s',
  },
  roleBadge: {
    position: 'absolute',
    top: '6px',
    left: '6px',
    background: 'rgba(0,0,0,0.7)',
    color: '#fff',
    fontSize: '0.65rem',
    padding: '2px 6px',
    borderRadius: '4px',
    fontWeight: 600,
  },
  commentBubble: {
    position: 'absolute',
    top: '6px',
    right: '6px',
    background: '#6366f1',
    color: '#fff',
    fontSize: '0.6rem',
    minWidth: '18px',
    height: '18px',
    borderRadius: '9px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    padding: '0 4px',
  },
};

/* ------------------------------------------------------------------ */
/*  Lightbox                                                           */
/* ------------------------------------------------------------------ */

function LightboxOverlay({
  state,
  comments,
  commentInput,
  submitting,
  readOnly,
  t,
  onClose,
  onNavigate,
  onCommentChange,
  onCommentSubmit,
}: {
  state: LightboxState;
  comments: AssetShareComment[];
  commentInput: string;
  submitting: boolean;
  readOnly: boolean;
  t: (key: string, opts?: Record<string, unknown>) => string;
  onClose: () => void;
  onNavigate: (index: number) => void;
  onCommentChange: (value: string) => void;
  onCommentSubmit: () => void;
}) {
  const current = state.images[state.index];
  const currentImageId =
    state.section === 'casting'
      ? `casting:${current.imageHash}`
      : current.id;

  const [imageLoading, setImageLoading] = useState(true);
  const currentSrc = tosShareImage(current.imageUrl, 800);

  useEffect(() => {
    setImageLoading(true);
  }, [currentSrc]);

  const imageComments = useMemo(
    () => comments.filter(c => c.assetImageId === currentImageId),
    [comments, currentImageId],
  );

  const hasPrev = state.index > 0;
  const hasNext = state.index < state.images.length - 1;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && hasPrev) onNavigate(state.index - 1);
      if (e.key === 'ArrowRight' && hasNext) onNavigate(state.index + 1);
    },
    [hasPrev, hasNext, onClose, onNavigate, state.index],
  );

  const sectionLabel =
    state.section === 'casting'
      ? t('assetShare.casting')
      : state.section;

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div style={lbStyles.overlay} onKeyDown={handleKeyDown} tabIndex={-1}>
      {/* Top bar */}
      <div style={lbStyles.topBar}>
        <button onClick={onClose} style={lbStyles.closeBtn}>
          &times;
        </button>
        <span style={lbStyles.title}>
          {state.characterName} &middot; {sectionLabel}
        </span>
        <span style={lbStyles.pageIndicator}>
          {state.index + 1}/{state.images.length}
        </span>
      </div>

      {/* Main image area */}
      <div style={lbStyles.mainArea}>
        {hasPrev && (
          <button
            style={{ ...lbStyles.navBtn, left: '8px' }}
            onClick={() => onNavigate(state.index - 1)}
          >
            &#8249;
          </button>
        )}
        {imageLoading && (
          <div style={lbStyles.spinnerWrap}>
            <div style={lbStyles.lbSpinner} />
          </div>
        )}
        <img
          src={currentSrc}
          alt={`${state.characterName} ${sectionLabel}`}
          style={{ ...lbStyles.mainImage, opacity: imageLoading ? 0 : 1, transition: 'opacity 0.2s' }}
          onLoad={() => setImageLoading(false)}
        />
        {hasNext && (
          <button
            style={{ ...lbStyles.navBtn, right: '8px' }}
            onClick={() => onNavigate(state.index + 1)}
          >
            &#8250;
          </button>
        )}
      </div>

      {/* Thumbnail strip */}
      {state.images.length > 1 && (
        <div className="asp-thumb-strip">
          {state.images.map((img, idx) => (
            <img
              key={img.id}
              src={tosShareImage(img.imageUrl, 60)}
              alt=""
              onClick={() => onNavigate(idx)}
              style={{
                ...lbStyles.thumb,
                border: idx === state.index ? '2px solid #6366f1' : '2px solid transparent',
                opacity: idx === state.index ? 1 : 0.5,
              }}
            />
          ))}
        </div>
      )}

      {/* Comments section */}
      <div style={lbStyles.commentSection}>
        {imageComments.length === 0 ? (
          <p style={lbStyles.noComments}>{t('assetShare.noComments')}</p>
        ) : (
          <div style={lbStyles.commentList}>
            {imageComments.map(c => (
              <div key={c.id} style={lbStyles.commentItem}>
                <p style={lbStyles.commentText}>{c.content}</p>
                <span style={lbStyles.commentTime}>
                  {new Date(c.createdAt).toLocaleString('zh-CN')}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Comment input */}
        {readOnly ? (
          <p style={lbStyles.noComments}>{t('assetShare.commentsClosed')}</p>
        ) : (
          <div style={lbStyles.inputRow}>
            <input
              type="text"
              value={commentInput}
              onChange={e => onCommentChange(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey && commentInput.trim() && !submitting) {
                  e.preventDefault();
                  onCommentSubmit();
                }
              }}
              placeholder={t('assetShare.commentPlaceholder')}
              style={lbStyles.input}
            />
            <button
              onClick={onCommentSubmit}
              disabled={!commentInput.trim() || submitting}
              style={{
                ...lbStyles.submitBtn,
                opacity: !commentInput.trim() || submitting ? 0.5 : 1,
                cursor: !commentInput.trim() || submitting ? 'not-allowed' : 'pointer',
              }}
            >
              {submitting ? t('assetShare.submitting') : t('assetShare.submitBtn')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const lbStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: '#000',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    outline: 'none',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    gap: '12px',
    flexShrink: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#fff',
    fontSize: '1.5rem',
    cursor: 'pointer',
    padding: '0 4px',
    lineHeight: 1,
  },
  title: {
    flex: 1,
    color: '#ccc',
    fontSize: '0.85rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  pageIndicator: {
    color: '#888',
    fontSize: '0.8rem',
    flexShrink: 0,
  },
  mainArea: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    minHeight: 0,
  },
  mainImage: {
    maxWidth: '90%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
  spinnerWrap: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  lbSpinner: {
    width: '32px',
    height: '32px',
    border: '3px solid rgba(255,255,255,0.2)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  navBtn: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'rgba(255,255,255,0.15)',
    border: 'none',
    color: '#fff',
    fontSize: '2rem',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  },
  thumb: {
    width: '48px',
    height: '48px',
    objectFit: 'cover',
    borderRadius: '4px',
    cursor: 'pointer',
    flexShrink: 0,
  },
  commentSection: {
    flexShrink: 0,
    maxHeight: '35vh',
    overflowY: 'auto',
    borderTop: '1px solid #222',
    padding: '8px 16px 16px',
  },
  noComments: {
    color: '#666',
    fontSize: '0.8rem',
    margin: '4px 0 8px',
  },
  commentList: {
    marginBottom: '8px',
  },
  commentItem: {
    padding: '6px 0',
    borderBottom: '1px solid #1a1a1a',
  },
  commentText: {
    margin: 0,
    color: '#ddd',
    fontSize: '0.85rem',
    lineHeight: 1.4,
  },
  commentTime: {
    fontSize: '0.7rem',
    color: '#555',
  },
  inputRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  input: {
    flex: 1,
    background: '#111',
    border: '1px solid #333',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '0.85rem',
    padding: '8px 10px',
    fontFamily: 'inherit',
    outline: 'none',
  },
  submitBtn: {
    background: '#6366f1',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 14px',
    fontSize: '0.85rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
};

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function AssetSharePreview() {
  const { code } = useParams<{ code: string }>();
  const { t } = useTranslation('profile');

  const [data, setData] = useState<AssetSharePreviewData | null>(null);
  const [comments, setComments] = useState<AssetShareComment[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [lightbox, setLightbox] = useState<LightboxState | null>(null);
  const [commentInput, setCommentInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Inject responsive styles once
  useEffect(() => {
    ensureResponsiveStyles();
  }, []);

  // Fetch data
  useEffect(() => {
    if (!code) return;
    Promise.all([
      getAssetSharePreview(code),
      getAssetShareComments(code),
    ])
      .then(([previewData, commentsData]) => {
        setData(previewData);
        setComments(commentsData);
        setLoading(false);
      })
      .catch(e => {
        setError(e.message);
        setLoading(false);
      });
  }, [code]);

  const toggleCollapse = useCallback((name: string) => {
    setCollapsed(prev => ({ ...prev, [name]: !prev[name] }));
  }, []);

  const openLightbox = useCallback(
    (characterName: string, section: string, images: LightboxImage[], index: number) => {
      setLightbox({ characterName, section, images, index });
      setCommentInput('');
    },
    [],
  );

  const navigateLightbox = useCallback((index: number) => {
    setLightbox(prev => (prev ? { ...prev, index } : null));
    setCommentInput('');
  }, []);

  const closeLightbox = useCallback(() => {
    setLightbox(null);
    setCommentInput('');
  }, []);

  const readOnly = !!data?.readOnly;

  const handleCommentSubmit = useCallback(async () => {
    if (!code || !lightbox || !commentInput.trim() || submitting || readOnly) return;

    const current = lightbox.images[lightbox.index];
    const assetImageId =
      lightbox.section === 'casting'
        ? `casting:${current.imageHash}`
        : current.id;

    setSubmitting(true);
    try {
      const newComment = await createAssetShareComment(code, {
        category: lightbox.section === 'casting' ? 'casting' : 'styling',
        assetName: lightbox.characterName,
        assetState: lightbox.section === 'casting' ? '' : lightbox.section,
        assetImageId,
        content: commentInput.trim(),
      });
      setComments(prev => [...prev, newComment]);
      setCommentInput('');
    } catch {
      // Keep input so user can retry
    } finally {
      setSubmitting(false);
    }
  }, [code, lightbox, commentInput, submitting, readOnly]);

  /* ------ Loading / Error / Empty states ------ */

  if (loading) {
    return (
      <div style={pageStyles.center}>
        <div style={pageStyles.spinner} />
        <p style={{ color: '#999', marginTop: '1rem' }}>{t('assetShare.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageStyles.center}>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
            {error.includes('expired') ? '⌛' : error.includes('view limit') ? '\u{1F6AB}' : '⚠️'}
          </div>
          <h2 style={{ color: '#fff', margin: '0 0 0.5rem', fontSize: '1.25rem' }}>
            {error.includes('expired')
              ? t('assetShare.linkExpired')
              : error.includes('view limit')
                ? t('assetShare.viewLimitReached')
                : t('assetShare.cannotAccess')}
          </h2>
          <p style={{ color: '#888', fontSize: '0.9rem' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={pageStyles.center}>
        <p style={{ color: '#888' }}>{t('assetShare.emptyContent')}</p>
      </div>
    );
  }

  const formattedExpiry = data.expiresAt
    ? new Date(data.expiresAt).toLocaleString('zh-CN')
    : '';

  const characters = data.characters || [];

  return (
    <div className="asp-page" style={pageStyles.page}>
      {/* Header */}
      <div style={pageStyles.header}>
        <p style={pageStyles.label}>{data.label}</p>
        <h1 style={pageStyles.projectName}>{data.projectName}</h1>
        {formattedExpiry && (
          <p style={pageStyles.expiry}>
            {t('assetShare.validUntil', { date: formattedExpiry })}
          </p>
        )}
      </div>

      {/* Character cards */}
      {characters.length === 0 ? (
        <p style={pageStyles.emptyMsg}>{t('assetShare.noCharacters')}</p>
      ) : (
        <div style={pageStyles.cardList}>
          {characters.map(char => (
            <CharacterCard
              key={char.name}
              character={char}
              isCollapsed={!!collapsed[char.name]}
              comments={comments}
              t={t}
              onToggle={() => toggleCollapse(char.name)}
              onImageClick={openLightbox}
            />
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <LightboxOverlay
          state={lightbox}
          comments={comments}
          commentInput={commentInput}
          submitting={submitting}
          readOnly={readOnly}
          t={t}
          onClose={closeLightbox}
          onNavigate={navigateLightbox}
          onCommentChange={setCommentInput}
          onCommentSubmit={handleCommentSubmit}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  CharacterCard                                                      */
/* ------------------------------------------------------------------ */

function CharacterCard({
  character,
  isCollapsed,
  comments,
  t,
  onToggle,
  onImageClick,
}: {
  character: AssetShareCharacter;
  isCollapsed: boolean;
  comments: AssetShareComment[];
  t: (key: string, opts?: Record<string, unknown>) => string;
  onToggle: () => void;
  onImageClick: (characterName: string, section: string, images: LightboxImage[], index: number) => void;
}) {
  const faceGroups = character.faceGroups || [];

  return (
    <div style={charStyles.card}>
      {/* Character header */}
      <div style={charStyles.header} onClick={onToggle}>
        <div style={charStyles.purpleBar} />
        <span style={charStyles.name}>{character.name}</span>
        <span style={charStyles.chevron}>{isCollapsed ? '▶' : '▼'}</span>
      </div>

      {!isCollapsed && (
        <div style={charStyles.body}>
          {faceGroups.map((face, faceIdx) => {
            const castingImageId = face.casting?.imageHash
              ? `casting:${face.casting.imageHash}`
              : '';
            const castingCommentCount = castingImageId
              ? countCommentsForImage(comments, castingImageId)
              : 0;

            return (
              <div key={face.casting?.imageHash || faceIdx} style={faceIdx > 0 ? { marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #2a2a2a' } : undefined}>
                {/* Casting (face) image */}
                {face.casting?.imageUrl && (
                  <div style={charStyles.section}>
                    <div style={charStyles.sectionHeader}>
                      <span style={charStyles.castingDot} />
                      <span style={charStyles.sectionLabel}>
                        {t('assetShare.casting')}{faceGroups.length > 1 ? ` ${faceIdx + 1}` : ''}
                      </span>
                    </div>
                    <div className="asp-grid">
                      <ImageCard
                        src={face.casting.imageUrl}
                        alt={`${character.name} casting`}
                        commentCount={castingCommentCount}
                        onClick={() =>
                          onImageClick(character.name, 'casting', [
                            {
                              id: `casting:${face.casting.imageHash}`,
                              imageUrl: face.casting.imageUrl,
                              imageHash: face.casting.imageHash,
                              role: '',
                            },
                          ], 0)
                        }
                      />
                    </div>
                  </div>
                )}

                {/* Styling sections under this face */}
                {face.stylings?.map(sg => {
                  const stateImages: LightboxImage[] = sg.results.map(r => ({
                    id: r.id,
                    imageUrl: r.imageUrl,
                    imageHash: r.imageHash,
                    role: r.role,
                  }));

                  return (
                    <div key={sg.state} style={charStyles.section}>
                      <div style={charStyles.sectionHeader}>
                        <span style={charStyles.stylingDot} />
                        <span style={charStyles.sectionLabel}>
                          {t('assetShare.styling')} &middot; {sg.state}
                        </span>
                      </div>
                      <div className="asp-grid">
                        {sg.results.map((result, idx) => {
                          const imgCommentCount = countCommentsForImage(comments, result.id);
                          const roleBadge =
                            result.role === 'primary'
                              ? t('assetShare.primary')
                              : result.role === 'alternate'
                                ? t('assetShare.alternate')
                                : undefined;

                          return (
                            <ImageCard
                              key={result.id}
                              src={result.imageUrl}
                              alt={`${character.name} ${sg.state}`}
                              roleBadge={roleBadge}
                              commentCount={imgCommentCount}
                              onClick={() =>
                                onImageClick(character.name, sg.state, stateImages, idx)
                              }
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const charStyles: Record<string, React.CSSProperties> = {
  card: {
    background: '#161616',
    borderRadius: '10px',
    marginBottom: '12px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 14px',
    cursor: 'pointer',
    gap: '10px',
    position: 'sticky',
    top: 0,
    zIndex: 5,
    background: '#161616',
  },
  purpleBar: {
    width: '4px',
    height: '20px',
    borderRadius: '2px',
    background: '#7c3aed',
    flexShrink: 0,
  },
  name: {
    flex: 1,
    fontWeight: 600,
    fontSize: '0.95rem',
    color: '#c4b5fd',
  },
  chevron: {
    color: '#666',
    fontSize: '0.7rem',
    flexShrink: 0,
  },
  body: {
    padding: '0 14px 14px',
  },
  section: {
    marginBottom: '14px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  castingDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#f59e0b',
    flexShrink: 0,
  },
  stylingDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#a78bfa',
    flexShrink: 0,
  },
  sectionLabel: {
    fontSize: '0.8rem',
    color: '#999',
    fontWeight: 500,
  },
};

/* ------------------------------------------------------------------ */
/*  Page-level styles                                                  */
/* ------------------------------------------------------------------ */

const pageStyles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: '#0a0a0a',
    color: '#fff',
    maxWidth: '100%',
    margin: '0 auto',
    paddingBottom: '2rem',
  },
  center: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#0a0a0a',
    color: '#fff',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #fff',
    borderTopColor: 'transparent',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  header: {
    padding: '1.25rem 1rem 0.75rem',
    borderBottom: '1px solid #222',
  },
  label: {
    margin: '0 0 0.25rem',
    fontSize: '0.8rem',
    color: '#888',
  },
  projectName: {
    margin: 0,
    fontSize: '1.2rem',
    fontWeight: 700,
  },
  expiry: {
    margin: '0.35rem 0 0',
    fontSize: '0.75rem',
    color: '#666',
  },
  cardList: {
    padding: '0.75rem 1rem',
  },
  emptyMsg: {
    textAlign: 'center',
    color: '#666',
    padding: '3rem 1rem',
    fontSize: '0.9rem',
  },
};
